/**
 * PATH: apps/quote/src/App.tsx
 * PURPOSE: Main application component for Quote Builder Hub
 * ROLE IN ARCHITECTURE: Root component with routing and layout
 *
 * FEATURES:
 * - Uses @quotech/auth for SSO, session management, and protected routes
 * - Uses @quotech/ui DashboardLayout for consistent UI with other hubs
 * - Session monitoring with automatic redirect to master login on expiration
 * - Integration status syncing (Xero, Simpro)
 *
 * SCROLL ARCHITECTURE:
 * - Builder routes (/builder/*) use single-scroll model: outer layout doesn't scroll
 * - Builder workspace owns its own scroll container for Excel-like behavior
 * - All other routes use normal DashboardLayout scrolling
 */

import { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { 
  DashboardLayout,
  TabBar,
  buildSettingsTabs,
  ErrorBoundary,
  useTabNavigation,
  useTabKeyboardShortcuts,
  VersionWatcher,
} from '@quotech/ui';
import { 
  ProtectedRoute, 
  HubGuard,
  SSOHandler, 
  useAuth,
  useComputedIsAdmin,
  useRBAC,
  startSessionMonitoring,
  stopSessionMonitoring,
  syncIntegrationStatus,
  UnifiedSSOPage,
  useAuthStore,
  getRefreshCookie,
} from '@quotech/auth';
import { useApiClient } from '@quotech/api';
import { 
  MODULE_CONFIG, 
  NAV_ITEMS, 
  SECONDARY_NAV_ITEMS, 
  EXTERNAL_LINKS,
  QUICK_ADD_ITEMS,
  COMMANDS,
  HUB_LINKS,
} from './config/navigation';
import { QUOTE_TAB_CONFIG, useQuoteTabStore } from './config/tabs';

// Page components
import DashboardPage from './pages/DashboardPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import ImportQuotePage from './pages/import-quote';
import BuilderPage from './pages/BuilderPage';
import BuilderLanding from './pages/builder/BuilderLanding';
import BuilderCreate from './pages/builder/BuilderCreate';
import PricelistPage from './pages/PricelistPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierQuotesPage from './pages/SupplierQuotesPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateDetailPage from './pages/TemplateDetailPage';
import SettingsPage from './pages/SettingsPage';
import { ClaimsListPage, ClaimEditorPage, NewClaimPage } from './pages/claims';

/**
 * Layout wrapper with DashboardLayout
 * 
 * Builder routes use special handling:
 * - contentClassName="overflow-hidden" prevents outer scroll (single-scroll model)
 * - quietMode auto-collapses sidebar and reduces header noise for focus
 */
function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const apiClient = useApiClient();
  
  // Determine if we're on a builder route that needs single-scroll model + quiet mode
  const isBuilderRoute = location.pathname.startsWith('/builder');
  const isBuilderWorkspace = location.pathname.match(/^\/builder\/[^/]+$/);
  
  // Enable quiet mode for builder workspace (collapsed sidebar, compact header, no search)
  const quietMode = !!isBuilderWorkspace;

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null);
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC();
  
  // Build settings tabs using universal helper
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name,
      role: user.role,
      isAdmin: computedIsAdmin,
      admin: (user as any).admin,
      is_admin: (user as any).is_admin,
      is_staff: (user as any).is_staff,
      is_superuser: (user as any).is_superuser,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'quote',
    hubName: 'Quote Builder',
    apiClient,
    organisationId: user?.organisation_id || '',
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name,
    },
    shortcuts: [
      { label: 'Save quote', keys: ['⌘', 'S'], category: 'Quote Builder' },
      { label: 'Preview quote', keys: ['⌘', 'P'], category: 'Quote Builder' },
      { label: 'Move to next cell', keys: ['Tab'], category: 'Quote Builder' },
    ],
  }), [user, apiClient, computedIsAdmin, rbacPermissions, hierarchyLevel]);
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ), []
  );

  const tabNav = useTabNavigation(QUOTE_TAB_CONFIG)
  useTabKeyboardShortcuts({ useStore: useQuoteTabStore, ...tabNav })

  // Header user carries the authority display email. Hoisted to a variable so
  // the explicit `displayEmail` key isn't rejected by object-literal
  // excess-property checks against the shared UI User prop. Login identity
  // (JWT / account / sender) stays on `email`.
  const headerUser = user ? { ...user, displayEmail: user.display_email || user.authority_email || user.email } : undefined

  return (
    <DashboardLayout
      module={MODULE_CONFIG}
      currentHub="quote"
      accessibleModules={accessibleModules}
      navItems={NAV_ITEMS}
      secondaryNavItems={secondaryNavWithSettingsAction}
      externalLinks={EXTERNAL_LINKS}
      quickAddItems={QUICK_ADD_ITEMS}
      commands={COMMANDS}
      hubLinks={HUB_LINKS}
      user={headerUser}
      onLogout={logout}
      showHubSwitcher={true}
      showNotifications={true}
      showQuickAdd={true}
      showCommandPalette={true}
      sidebarSectionLabels={{
        main: 'Quote Builder',
        secondary: 'Tools',
        quickAccess: 'Quick Access',
        hubs: 'Other Hubs',
      }}
      contentClassName={isBuilderRoute ? 'overflow-hidden' : undefined}
      quietMode={quietMode}
      headerTabsSlot={
        <TabBar
          variant="inline"
          tabs={tabNav.tabItems}
          activeTabId={tabNav.activeTabId}
          onTabClick={tabNav.handleTabClick}
          onTabClose={tabNav.handleTabClose}
          onNewTab={tabNav.handleNewTab}
          onTabPin={tabNav.handleTabPin}
          onCloseOthers={tabNav.handleCloseOthers}
          onDuplicateTab={tabNav.handleDuplicateTab}
          accentColor="bg-amber-500"
        />
      }
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
    >
      <Outlet />
    </DashboardLayout>
  );
}

function App() {
  // =========================================================================
  // SESSION MONITORING (unified across all hubs)
  // =========================================================================
  useEffect(() => {
    const manager = startSessionMonitoring({
      onSessionExpired: (reason) => {
      },
      onTokenRefreshed: (newToken) => {
        
        // CRITICAL: Update Zustand store with the new token from SessionManager
        const refreshToken = getRefreshCookie()
        useAuthStore.getState().updateTokens(newToken, refreshToken || undefined)
        
        syncIntegrationStatus(newToken).catch(err => {
          console.warn('[Quote Builder] Failed to sync integration status:', err)
        })
      },
    })

    syncIntegrationStatus().catch(err => {
      console.warn('[Quote Builder] Failed to sync integration status:', err)
    })

    return () => {
      stopSessionMonitoring()
    }
  }, [])

  return (
    <ErrorBoundary>
    {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
    <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
    <Routes>
      {/* Unified SSO Route */}
      <Route
        path="/sso"
        element={
          <UnifiedSSOPage 
            defaultRedirectPath="/builder"  // Quote Builder landing page
            loadingComponent={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to Quote Builder...</p>
                </div>
              </div>
            }
          />
        }
      />

      {/* Protected routes with unified layout */}
      <Route
        element={
          <ProtectedRoute useMasterLogin>
            <HubGuard hub="quote"><AppLayout /></HubGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/:id" element={<QuoteDetailPage />} />
        <Route path="/import" element={<ImportQuotePage />} />
        {/* Builder routes - Landing, New, and Workspace */}
        <Route path="/builder" element={<BuilderLanding />} />
        <Route path="/builder/new" element={<BuilderCreate />} />
        <Route path="/builder/:id" element={<BuilderPage />} />
        <Route path="/pricelist" element={<PricelistPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/supplier-quotes" element={<SupplierQuotesPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/:templateId" element={<TemplateDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/claims" element={<ClaimsListPage />} />
        {/* /claims/new must precede /claims/:id so "new" is not read as a pk */}
        <Route path="/claims/new" element={<NewClaimPage />} />
        <Route path="/claims/:id" element={<ClaimEditorPage />} />
      </Route>

      {/* Default redirects - go straight to Builder */}
      <Route path="/" element={<Navigate to="/builder" replace />} />
      <Route path="*" element={<Navigate to="/builder" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default App;

